import _ from 'lodash';
import { MenuItem } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { Trans } from '@lingui/macro';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import {
    CAC_LDAP_INEXACT_FIELD,
    CAC_LDAP_BIND_DN_FIELD,
    CAC_LDAP_DEFAULT_ROLE_FIELD,
    CAC_LDAP_ENCRYPTED_BIND_CREDENTIALS_FIELD,
    CAC_LDAP_GROUP_ROLE_ASSOCIATIONS_FIELD,
    CAC_LDAP_PORT_FIELD,
    CAC_LDAP_SECURE_FIELD,
    CAC_LDAP_SERVER_FIELD,
    CAC_LDAP_TLS_REJECT_UNAUTHORIZED_FIELD,
    CAC_LDAP_USER_SEARCH_BASE_FIELD,
    CAC_LDAP_USER_USERNAME_ATTR_FIELD,
    UNAUTHORIZED_OPTION_TEXT,
    UNAUTHORIZED_OPTION_VALUE,
    CAC_LDAP_REVOCATION_METHOD,
    CAC_LDAP_REVOCATION_RESPONDER,
    CAC_LDAP_REVOCATION_RESOLVER,
    CAC_LDAP_REVOCATION_METHOD_NONE,
    CAC_LDAP_REVOCATION_METHOD_OCSP,
    CAC_LDAP_REVOCATION_METHOD_CRL,
    CAC_LDAP_REVOCATION_METHOD_NONE_KEY,
    CAC_LDAP_REVOCATION_METHOD_OCSP_KEY,
    CAC_LDAP_REVOCATION_METHOD_CRL_KEY,
} from './SsoAdminConstants';
import NgpTextField from '../../form/NgpTextField';
import { AUTO_COMPLETE_DEFAULT } from '../../../utilities/FormUtilities';
import NgpNumberField from '../../form/NgpNumberField';
import NgpRow from '../../form/NgpRow';
import NgpPasswordField from '../../form/NgpPasswordField';
import NgpSelect from '../../form/NgpSelect';
import ListFormField from '../../form/ListFormField';
import { getGroupAssociationColumns } from './SsoAdminHelper';
import GroupForm from './GroupForm';

const URI_REGEX = new RegExp(/^(http:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+){1,5}(:[0-9]{1,5})?([a-z]*\/?)*$/);
const groupAssociationColumns = getGroupAssociationColumns(true);

export default function CacLdap({ formData, userRoleOptions, hasCacCert, setDirty }) {
    const { watch } = useFormContext();
    const ldapSecure = watch(CAC_LDAP_SECURE_FIELD);
    const revMethod = watch(CAC_LDAP_REVOCATION_METHOD);
    const invalidHostname = `Value must be hostname or IP`;
    const revocationOptions = hasCacCert
        ? {
              [CAC_LDAP_REVOCATION_METHOD_NONE_KEY]: CAC_LDAP_REVOCATION_METHOD_NONE,
              [CAC_LDAP_REVOCATION_METHOD_OCSP_KEY]: CAC_LDAP_REVOCATION_METHOD_OCSP,
              [CAC_LDAP_REVOCATION_METHOD_CRL_KEY]: CAC_LDAP_REVOCATION_METHOD_CRL,
          }
        : {
              [CAC_LDAP_REVOCATION_METHOD_NONE_KEY]: CAC_LDAP_REVOCATION_METHOD_NONE,
              [CAC_LDAP_REVOCATION_METHOD_OCSP_KEY]: CAC_LDAP_REVOCATION_METHOD_OCSP,
          };

    function validateHostname(value) {
        return !value || (_.isString(value) && value.match(URI_REGEX)) ? true : invalidHostname;
    }

    return (
        <>
            <FormSection
                title={<Trans>Server Configuration</Trans>}
                subText={
                    <Trans>This section defines how nGeniusPULSE will connect to your Active Directory server</Trans>
                }
            >
                <NgpToggle
                    fieldName={CAC_LDAP_SECURE_FIELD}
                    label={<Trans>Secure</Trans>}
                    helpTipText={
                        <Trans>
                            Active Directory traffic secured via TLS 1.2. Disabling this uses basic authentication,
                            which sends unencrypted credentials.
                        </Trans>
                    }
                />

                {ldapSecure && (
                    <NgpToggle
                        fieldName={CAC_LDAP_TLS_REJECT_UNAUTHORIZED_FIELD}
                        label={<Trans>Validate Server Certificate</Trans>}
                        helpTipText={
                            <Trans>
                                Requires that the Active Directory server certificate has been signed by a valid CA. If
                                the Active Directory server has a self-signed certificate, this option should be
                                disabled.
                            </Trans>
                        }
                    />
                )}

                <NgpRow>
                    <NgpTextField
                        fieldName={CAC_LDAP_SERVER_FIELD}
                        label={<Trans>Server Hostname/IP</Trans>}
                        required
                        inputProps={{
                            size: 40,
                            maxLength: 4096,
                            autoComplete: AUTO_COMPLETE_DEFAULT,
                        }}
                    />

                    <NgpNumberField
                        fieldName={CAC_LDAP_PORT_FIELD}
                        label={<Trans>Port</Trans>}
                        min="1"
                        max="65535"
                        inputProps={{
                            placeholder: ldapSecure ? '636' : '389',
                            size: 22,
                            maxLength: 5,
                        }}
                    />
                </NgpRow>

                <NgpToggle
                    fieldName={CAC_LDAP_INEXACT_FIELD}
                    label={<Trans>Allow inexact LDAP matches</Trans>}
                    helpTipText={
                        <Trans>
                            When looking up user, allow an inexact match of CN or EDIPI from certificate to LDAP account
                        </Trans>
                    }
                />
                <NgpTextField
                    fieldName={CAC_LDAP_BIND_DN_FIELD}
                    label={<Trans>Bind DN</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={
                        <Trans>
                            The full distinguished name of the user to bind as on the Active Directory server. For
                            example, username &quot;admin&quot; may have a Bind DN of
                            &quot;CN=admin,BU=IT,OU=Corp,DC=mycompany,DC=com&quot;
                        </Trans>
                    }
                />

                <NgpPasswordField
                    fieldName={CAC_LDAP_ENCRYPTED_BIND_CREDENTIALS_FIELD}
                    label={<Trans>Bind Credentials</Trans>}
                    required
                    inputProps={{ size: 60, maxLength: 4096 }}
                    togglePasswordVisibility
                />
            </FormSection>

            <FormSection
                title={<Trans>User Configuration</Trans>}
                subText={
                    <Trans>
                        This section defines how to link users between your Active Directory server and nGeniusPULSE
                    </Trans>
                }
            >
                <NgpTextField
                    fieldName={CAC_LDAP_USER_SEARCH_BASE_FIELD}
                    label={<Trans>Search Base</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={
                        <Trans>
                            The location in the Active Directory tree to begin searching for users and groups. This is
                            typically the domain root. For example, &quot;company.com&quot; could have a search base of
                            &quot;DC=company,DC=com&quot;
                        </Trans>
                    }
                />

                <NgpTextField
                    fieldName={CAC_LDAP_USER_USERNAME_ATTR_FIELD}
                    label={<Trans>Username Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={
                        <Trans>
                            The Active Directory attribute which contains a user&apos;s username. Most configurations
                            will use &quot;sAMAccountName&quot; unless the Active Directory administrator has chosen to
                            use another attribute.
                        </Trans>
                    }
                />

                <NgpSelect fieldName={CAC_LDAP_DEFAULT_ROLE_FIELD} label={<Trans>Default User Role</Trans>}>
                    <MenuItem key={1} value={UNAUTHORIZED_OPTION_VALUE}>
                        {UNAUTHORIZED_OPTION_TEXT}
                    </MenuItem>
                    {_.map(userRoleOptions, (value, key) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </NgpSelect>
            </FormSection>

            <FormSection
                title={<Trans>Certificate Status</Trans>}
                subText={<Trans>This section defines how to configure certificate status checking</Trans>}
            >
                <NgpSelect fieldName={CAC_LDAP_REVOCATION_METHOD} label={<Trans>Status checking</Trans>}>
                    {_.map(revocationOptions, (value, key) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </NgpSelect>
                {revMethod === CAC_LDAP_REVOCATION_METHOD_OCSP_KEY && (
                    <NgpTextField
                        fieldName={CAC_LDAP_REVOCATION_RESPONDER}
                        label={<Trans>OCSP Responder</Trans>}
                        inputProps={{
                            size: 60,
                            maxLength: 4096,
                            autoComplete: AUTO_COMPLETE_DEFAULT,
                        }}
                        fieldRules={{
                            validate: (value) => validateHostname(value),
                        }}
                    />
                )}
                {revMethod === CAC_LDAP_REVOCATION_METHOD_OCSP_KEY && (
                    <NgpTextField
                        fieldName={CAC_LDAP_REVOCATION_RESOLVER}
                        label={<Trans>DNS Resolver</Trans>}
                        inputProps={{
                            size: 60,
                            maxLength: 4096,
                            autoComplete: AUTO_COMPLETE_DEFAULT,
                        }}
                    />
                )}
            </FormSection>

            <FormSection
                title={<Trans>Group Configuration</Trans>}
                subText={
                    <Trans>
                        This section defines the relationship between the groups that users may belong to in Active
                        Directory and what role they will be given in nGeniusPULSE on first login
                    </Trans>
                }
                addSpacer
            >
                <ListFormField
                    fieldName={CAC_LDAP_GROUP_ROLE_ASSOCIATIONS_FIELD}
                    dbSelected={_.get(formData, CAC_LDAP_GROUP_ROLE_ASSOCIATIONS_FIELD)}
                    gridColumns={groupAssociationColumns}
                    setSelectionIsDirty={setDirty}
                    formComponent={<GroupForm isActiveDirectory />}
                />
            </FormSection>
        </>
    );
}
